import { useEffect, useRef, useState } from 'react'
import gsap from 'gsap'
import { ScrollTrigger } from 'gsap/ScrollTrigger'
import { MdEmail, MdPhone, MdLocationOn } from 'react-icons/md'
import { siInstagram, siFacebook, siYoutube } from 'simple-icons'
import './Contact.css'

const contactInfo = [
  { icon: <MdEmail size={35} color="#E4002B" />, label: 'Email Us', value: 'franchise@bigguys.in', link: 'mailto:franchise@bigguys.in' },
  { icon: <MdPhone size={35} color="#E4002B" />, label: 'Call Us', value: '(+91) 9986180513', link: 'tel:+919986180513' },
  { icon: <MdLocationOn size={35} color="#E4002B" />, label: 'Visit Us', value: 'Electronic City, Bengaluru, Karnataka', link: 'https://maps.google.com/?q=Electronic+City+Bangalore' },
]

const socialLinks = [
  { name: 'Instagram', url: 'https://www.instagram.com/bigguysindia/', icon: 'instagram' },
  { name: 'Facebook', url: 'https://www.facebook.com/profile.php?id=61556784862328', icon: 'facebook' },
  { name: 'YouTube', url: 'https://www.youtube.com/@BigguysIndia', icon: 'youtube' },
  { name: 'LinkedIn', url: 'https://www.linkedin.com/company/bigguys/', icon: 'linkedin' },
]

const renderSocialIcon = (iconType) => {
  switch(iconType) {
    case 'instagram':
      return (
        <svg role="img" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" width="28" height="28">
          <path fill="#FF0069" d={siInstagram.path} />
        </svg>
      )
    case 'facebook':
      return (
        <svg role="img" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" width="28" height="28">
          <path fill="#0866FF" d={siFacebook.path} />
        </svg>
      )
    case 'youtube':
      return (
        <svg role="img" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" width="28" height="28">
          <path fill="#FF0000" d={siYoutube.path} />
        </svg>
      )
    case 'linkedin':
      return (
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 640 640" width="28" height="28">
          <path fill="#0a66c2" d="M196.3 512L103.4 512L103.4 212.9L196.3 212.9L196.3 512zM149.8 172.1C120.1 172.1 96 147.5 96 117.8C96 103.5 101.7 89.9 111.8 79.8C121.9 69.7 135.6 64 149.8 64C164 64 177.7 69.7 187.8 79.8C197.9 89.9 203.6 103.6 203.6 117.8C203.6 147.5 179.5 172.1 149.8 172.1zM543.9 512L451.2 512L451.2 366.4C451.2 331.7 450.5 287.2 402.9 287.2C354.6 287.2 347.2 324.9 347.2 363.9L347.2 512L254.4 512L254.4 212.9L343.5 212.9L343.5 253.7L344.8 253.7C357.2 230.2 387.5 205.4 432.7 205.4C526.7 205.4 544 267.3 544 347.7L544 512L543.9 512z"/>
        </svg>
      )
    default: return null
  }
}

export default function Contact() {
  const [formData, setFormData] = useState({
    name: '', email: '', phone: '', message: ''
  })
  const [isSubmitting, setIsSubmitting] = useState(false)
  const pageRef = useRef(null)

  useEffect(() => {
    const ctx = gsap.context(() => {
      // Hero animation
      gsap.fromTo('.contact-hero-content > *',
        { opacity: 0, y: 50 },
        { opacity: 1, y: 0, duration: 0.8, stagger: 0.15, ease: 'power3.out', delay: 0.3 }
      )

      // Contact cards animation
      gsap.fromTo('.contact-card',
        { opacity: 0, y: 30 },
        {
          opacity: 1,
          y: 0,
          duration: 0.6,
          stagger: 0.1,
          ease: 'power3.out',
          scrollTrigger: {
            trigger: '.contact-cards',
            start: 'top 80%',
          }
        }
      )

      // Form animation
      gsap.fromTo('.contact-form-container',
        { opacity: 0, x: 30 },
        {
          opacity: 1,
          x: 0,
          duration: 0.8,
          ease: 'power3.out',
          scrollTrigger: {
            trigger: '.contact-content',
            start: 'top 70%',
          }
        }
      )

      // Social links animation
      gsap.fromTo('.social-link',
        { opacity: 0, scale: 0.8 },
        {
          opacity: 1,
          scale: 1,
          duration: 0.5,
          stagger: 0.1,
          ease: 'power3.out',
          scrollTrigger: {
            trigger: '.social-section',
            start: 'top 80%',
          }
        }
      )
    }, pageRef)

    return () => ctx.revert()
  }, [])

  const handleSubmit = async (e) => {
    e.preventDefault()
    setIsSubmitting(true)
    
    // Simulate form submission
    await new Promise(resolve => setTimeout(resolve, 1000))
    
    alert('Thank you for reaching out! We\'ll get back to you soon.')
    setFormData({ name: '', email: '', phone: '', message: '' })
    setIsSubmitting(false)
  }

  return (
    <div className="contact-page" ref={pageRef}>
      {/* Hero Section */}
      <section className="contact-hero">
        <div className="container">
          <div className="contact-hero-content">
            <span className="hero-label">Contact Us</span>
            <h1 className="hero-title">
              Let's Talk
              <span className="highlight"> Chicken</span>
            </h1>
            <p className="hero-description">
              Got questions? Want to partner with us? We'd love to hear from you.
            </p>
          </div>
        </div>
      </section>

      {/* Contact Cards */}
      <section className="contact-cards-section">
        <div className="container">
          <div className="contact-cards">
            {contactInfo.map((item, index) => (
              <a href={item.link} target="_blank" rel="noopener noreferrer" className="contact-card" key={index}>
                <span className="contact-icon">{item.icon}</span>
                <span className="contact-label">{item.label}</span>
                <span className="contact-value">{item.value}</span>
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Form Section */}
      <section className="contact-form-section">
        <div className="container">
          <div className="contact-content">
            <div className="contact-info-side">
              <span className="section-label">Get in Touch</span>
              <h2 className="section-title">Wing Us a Message</h2>
              <p>
                Whether you have a question about our menu, franchise opportunities, 
                or anything else, our team is ready to help.
              </p>

              <div className="office-info">
                <h3>Head Office</h3>
                <p className="office-company">Beamer Brands Pvt Ltd</p>
                <p className="office-address">
                  Electronic City, 2nd Cross Road<br />
                  Shanthi Pura, Phase 2<br />
                  Bengaluru, Karnataka, India
                </p>
              </div>

              <div className="working-hours">
                <h3>Working Hours</h3>
                <p>Monday - Saturday: 10:00 AM - 7:00 PM</p>
                <p>Sunday: Closed</p>
              </div>
            </div>

            <div className="contact-form-container">
              <form onSubmit={handleSubmit} className="contact-form">
                <div className="form-group">
                  <label className="form-label">Full Name *</label>
                  <input
                    type="text"
                    className="form-input"
                    required
                    value={formData.name}
                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                  />
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label className="form-label">Email *</label>
                    <input
                      type="email"
                      className="form-input"
                      required
                      value={formData.email}
                      onChange={(e) => setFormData({...formData, email: e.target.value})}
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Phone *</label>
                    <input
                      type="tel"
                      className="form-input"
                      required
                      value={formData.phone}
                      onChange={(e) => setFormData({...formData, phone: e.target.value})}
                    />
                  </div>
                </div>
                <div className="form-group">
                  <label className="form-label">Message *</label>
                  <textarea
                    className="form-textarea"
                    rows="5"
                    required
                    value={formData.message}
                    onChange={(e) => setFormData({...formData, message: e.target.value})}
                  ></textarea>
                </div>
                <button type="submit" className="btn btn-primary btn-full" disabled={isSubmitting}>
                  {isSubmitting ? 'Sending...' : 'Send Message'}
                </button>
              </form>
            </div>
          </div>
        </div>
      </section>

      {/* Social Section */}
      <section className="social-section">
        <div className="container">
          <div className="social-content">
            <h2>Follow Us On Social Media</h2>
            <p>Stay updated with our latest news, offers, and behind-the-scenes content.</p>
            <div className="social-links">
              {socialLinks.map((social, index) => (
                <a
                  key={index}
                  href={social.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="social-link"
                >
                  <span className="social-icon">{renderSocialIcon(social.icon)}</span>
                  <span className="social-label">{social.name}</span>
                </a>
              ))}
            </div>
          </div>
        </div>
      </section>
    </div>
  )
}
